/**
 * Landing page for the backend's auth failures: /auth/error?reason=…
 *
 * `/auth/verify` on the backend redirects here when a magic link cannot be
 * honoured — expired, already clicked, or belonging to a deactivated
 * account. It sends the visitor back to the origin the link was requested
 * from, so a link born on Chouette fails on Chouette (backend
 * `_magic_redirect_base`, 2026-09-21). Without this route the catch-all
 * bounced them to "/", i.e. the login screen with no explanation of what
 * had just gone wrong.
 */
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, PageLayout, Card, Button, Headline, MutedText } from "../components/ui";
import { Logo } from "../components/Logo";
import { parseAuthErrorReason } from "../lib/authError";

export default function AuthError() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const reason = parseAuthErrorReason(params.get("reason"));

  return (
    <PageLayout>
      <Container width="narrow" className="flex min-h-screen flex-col justify-center gap-6 py-10">
        <div className="text-center">
          <Logo size={56} className="mb-3" />
          <Headline className="text-3xl">{t(`authFlow.error.${reason}.title`)}</Headline>
        </div>

        <Card>
          <div className="flex flex-col gap-4">
            <MutedText>{t(`authFlow.error.${reason}.body`)}</MutedText>
            <Button type="button" className="w-full" onClick={() => navigate("/login", { replace: true })}>
              {t("authFlow.error.retry")}
            </Button>
          </div>
        </Card>

        <p className="text-center text-violet-muted text-xs font-heading uppercase tracking-wide">
          {t("login.signature")}
        </p>
      </Container>
    </PageLayout>
  );
}
